import React, { useEffect, useState } from 'react';
import useMediaQuery from '../../hooks/useMediaQuery';
import MoldShotProgressGradient from '../shared/MoldShotProgressGradient';
import { Mold } from '../../types/mold';
import FormFieldWrapper from '../shared/FormFieldWrapper';
import { useCustomers } from '../../hooks/useCustomers';
import { noteMapping } from '../../constants/moldOrder';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import { getNestedValue } from '../../utils/object';
import { isFieldDisabledForUser } from '../../utils/formUtils';
import { useAuth } from '../../hooks/useAuth';
import { useAppState } from '@/context/AppStateContext';
import { getDynamicIcon } from '@/utils/icon';
import { DisplayFieldsGroup } from '@/models/appState';

// Custom function to save new insert change type values
const saveCustomInsertChangeType = (value: string) => {
    // Avoid duplicates and the special "other_custom_text" value
    if (!noteMapping.includes(value) && value !== 'other_custom_text') {
        // Add the new value to the noteMapping array
        noteMapping.unshift(value); // Add to beginning of array
    }
    return value;
};

interface ExtendedMold extends Mold {
    customerId?: number;
}

interface MoldInformationProps {
    mold: Mold;
    editedMold: ExtendedMold;
    isEditing: boolean;
    onFieldChange: (field: keyof ExtendedMold, value: any) => void;
    layout?: 'default' | 'expanded';
    fieldErrors?: Record<string, boolean>;
    showShotCountProgressBar?: boolean;
}

type TabKey = string;

const MoldInformation: React.FC<MoldInformationProps> = ({
    mold,
    editedMold,
    isEditing,
    onFieldChange,
    layout = 'default',
    fieldErrors = {},
    showShotCountProgressBar = true,
}) => {
    const [activeTab, setActiveTab] = useState<TabKey>('general');
    // Track expanded state per tab
    const [expandedTabs, setExpandedTabs] = useState<Record<TabKey, boolean>>({
        general: isEditing,
        dimensions: isEditing,
        insert: isEditing,
        location: isEditing,
        shotCount: isEditing,
    });
    const isMobile = useMediaQuery('(max-width: 767px)');
    const COLLAPSED_COUNT = isMobile ? 6 : 10;
    const { user } = useAuth();
    const { getMoldDisplayFieldGroups } = useAppState();
    const displayFieldGroups = getMoldDisplayFieldGroups();

    // Expand/collapse all tabs when isEditing changes
    useEffect(() => {
        setExpandedTabs({
            general: isEditing,
            dimensions: isEditing,
            insert: isEditing,
            location: isEditing,
            shotCount: isEditing,
        });
    }, [isEditing]);

    const handleSeeMoreToggle = (tab: TabKey) => {
        setExpandedTabs(prev => ({ ...prev, [tab]: !prev[tab] }));
    };
    const { customers } = useCustomers();

    const handleCustomerChange = (value: string) => {
        const newCustomer = customers.find(customer => customer.id === Number(value));
        if (newCustomer) {
            onFieldChange('customer', newCustomer);
        }
    };

    const displayFieldGroupsMapping: Record<TabKey, DisplayFieldsGroup> = displayFieldGroups.reduce(
        (acc: Record<string, DisplayFieldsGroup>, fieldGroup) => {
            const key = fieldGroup.key as TabKey;
            acc[key] = fieldGroup;
            return acc;
        },
        {}
    );

    const TabButton: React.FC<{ value: TabKey, label: string, icon: React.ElementType }> = ({ value, label, icon: Icon }) => (
        <button
            onClick={() => setActiveTab(value)}
            className={`flex-1 py-3 px-4 text-base font-medium flex items-center justify-center ${activeTab === value
                ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
        >
            <Icon className="mr-2" size={20} />
            <p className="hidden sm:block">{label}</p>
        </button>
    );

    const getGridSpace = () => {
        if (!isEditing) return 'gap-0';
        if (layout === 'default') {
            return 'gap-1';
        }
        return 'gap-4';
    }
    // Add gap-y-2 for more vertical space, and use custom row backgrounds
    const gridClasses = `grid grid-cols-1 md:grid-cols-2 ${getGridSpace()} gap-y-2`;

    const getValue = (key: string) => {
        if (key === 'customerId') {
            const customerId = editedMold.customerId ?? editedMold.customer?.id;
            const customer = customers.find(customer => Number(customer.id) === Number(customerId));
            return customer ? customer.id : '';
        }
        return getNestedValue(editedMold, key);
    }

    // Track previous showIf state for each field
    const prevShowIfRef = React.useRef<Record<string, boolean>>({});

    useEffect(() => {
        // For all fields in all tabs
        Object.entries(displayFieldGroupsMapping).forEach(([_tabKey, displayFieldGroup]) => {
            displayFieldGroup.fields.forEach(field => {
                if (typeof field.showIf === 'function') {
                    const key = String(field.key);
                    const prev = prevShowIfRef.current[key];
                    const now = field.showIf(editedMold);
                    // If it was visible and now hidden, reset
                    if (prev === true && now === false) {
                        if (getValue(key) !== undefined && getValue(key) !== null) {
                            onFieldChange(key as keyof ExtendedMold, undefined);
                        }
                    }
                    prevShowIfRef.current[key] = now;
                }
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editedMold]);

    return (
        <div className="flex-grow">
            <div className={`flex border-b ${layout === 'expanded' ? 'mb-5' : 'mb-2'} overflow-x-auto`}>
                {displayFieldGroups.map(fieldGroup => {
                    const iconConfig = { source: 'lucide', name: fieldGroup.uiConfig?.icon || 'Info' };
                    return (<TabButton
                        key={fieldGroup.key}
                        value={fieldGroup.key as TabKey}
                        label={fieldGroup.label}
                        icon={getDynamicIcon(iconConfig) || Info}
                    />);
                })}
            </div>

            {Object.keys(displayFieldGroupsMapping).map((tabKey) => {
                return (activeTab === tabKey ? (
                    <React.Fragment key={tabKey}>
                        {(() => {
                            // Filter visible fields
                            const visibleFields = displayFieldGroupsMapping[tabKey]
                                .fields
                                .filter((field) => !field.showIf || field.showIf(editedMold));
                            const isExpanded = expandedTabs[tabKey as TabKey];
                            const shouldCollapse = visibleFields.length > COLLAPSED_COUNT;
                            const fieldsToShow = shouldCollapse && !isExpanded
                                ? visibleFields.slice(0, COLLAPSED_COUNT)
                                : visibleFields;
                            return <>
                                <div className={gridClasses}>
                                    {fieldsToShow.map((field, idx) => {
                                        const rowIdx = Math.floor(idx / 2);
                                        const isLast = idx === fieldsToShow.length - 1 && shouldCollapse && !isExpanded;
                                        return (
                                            <div
                                                key={field.key as string | number}
                                                className={`
                                                    ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                                                    ${rowIdx % 2 === 0 ? 'md:bg-gray-50' : 'md:bg-white'}
                                                `}
                                            >
                                                <div className="flex items-center w-full">
                                                    <FormFieldWrapper
                                                        displayStyle='inline'
                                                        isEditing={isEditing}
                                                        config={field}
                                                        value={getValue(field.key as string)}
                                                        onChange={(value) => {
                                                            if (field.key === 'customerId') {
                                                                handleCustomerChange(value);
                                                            }
                                                            const formattedValue = field.type === 'number' ? Number(value) : value;
                                                            onFieldChange(field.key as keyof ExtendedMold, formattedValue)
                                                        }}
                                                        disabled={isFieldDisabledForUser(field, user?.role)}
                                                        hasError={fieldErrors?.[field.key as string] || false}
                                                    />
                                                    {isLast && (
                                                        <button
                                                            className="ml-auto flex items-center gap-1 text-blue-700 font-semibold text-sm px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 shadow-sm hover:bg-blue-100 hover:border-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200"
                                                            onClick={() => handleSeeMoreToggle(tabKey as TabKey)}
                                                            type="button"
                                                        >
                                                            <span>{isExpanded ? 'น้อยลง' : 'อื่นๆ'}</span>
                                                            {isExpanded ? (
                                                                <ChevronUp size={16} className="text-blue-400" />
                                                            ) : (
                                                                <ChevronDown size={16} className="text-blue-400" />
                                                            )}
                                                            {!isExpanded && (
                                                                <span className="text-xs text-gray-500 ml-1 font-normal">
                                                                    +{visibleFields.length - COLLAPSED_COUNT}
                                                                </span>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {/* If expanded, show the button after the last field */}
                                    {!isEditing && shouldCollapse && isExpanded && (
                                        <div className="col-span-1 md:col-span-2 flex justify-end">
                                            <button
                                                className="flex items-center gap-1 text-blue-700 font-semibold text-sm px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 shadow-sm hover:bg-blue-100 hover:border-blue-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200"
                                                onClick={() => handleSeeMoreToggle(tabKey as TabKey)}
                                                type="button"
                                            >
                                                <span>น้อยลง</span>
                                                <ChevronUp size={16} className="text-blue-400" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>;
                        })()}
                        {/* Shot Count in its own row */}
                        {displayFieldGroupsMapping[tabKey].uiConfig.showShotCountBar && showShotCountProgressBar && <div className="mt-4">
                            <p className="text-base font-medium text-gray-500">
                                จำนวนชอตสะสมในรอบล้าง
                            </p>
                            <div className="mt-2">
                                <MoldShotProgressGradient
                                    shotCount={mold.cycleShotCount}
                                    servicingAlertShotThreshold={
                                        mold.servicingAlertShotThreshold
                                    }
                                    servicingMaxShotThreshold={mold.servicingMaxShotThreshold}
                                    maxShotCount={mold.maxShotCount}
                                />
                            </div>
                        </div>}
                    </React.Fragment>
                ) : null)
            })}
        </div>
    );
};

export default MoldInformation;